import { useState, useEffect } from 'react';
import { CalendarClock, X, StickyNote, Trash2 } from 'lucide-react';
import { useToast } from '../components/Toast';
import api from '../Server';

// For created_at timestamps only (actual UTC timestamps)
function formatCreatedDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  const ist  = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata', hour12: true });
  const us   = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York', hour12: true });
  return { date, ist, us };
}

// Display booking_date — always extracts YYYY-MM-DD directly from the string.
// NEVER passes through new Date() to avoid timezone shift bugs.
// MySQL DATE columns often arrive as "2026-05-26T00:00:00.000Z" or "2026-05-25T18:30:00.000Z"
// Parsing those with new Date() shifts the date by timezone offset (shows 1 day behind).
// Fix: always read the YYYY-MM-DD digits directly from the raw string.
function displayBookingDate(dateStr) {
  if (!dateStr) return '—';
  const str = String(dateStr).trim();
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // Matches "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm:ss.sssZ"
  // Always take the date digits directly — never convert through Date object
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year  = isoMatch[1];
    const month = months[parseInt(isoMatch[2], 10) - 1];
    const day   = isoMatch[3];
    return `${day} ${month} ${year}`;
  }

  // "DD-MM-YYYY" or "DD/MM/YYYY"
  const dmyMatch = str.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})/);
  if (dmyMatch) {
    return `${dmyMatch[1].padStart(2,'0')} ${months[parseInt(dmyMatch[2], 10) - 1]} ${dmyMatch[3]}`;
  }

  return str;
}

// Display booking_time as-is — it's a user-entered time string like "11:00" or "11:00:00"
// Just trim seconds if present, no timezone conversion
function displayBookingTime(timeStr) {
  if (!timeStr) return '—';
  const str = String(timeStr).trim();
  // "HH:MM:SS" → "HH:MM"
  const match = str.match(/^(\d{1,2}:\d{2})/);
  return match ? match[1] : str;
}

function NotesModal({ demo, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Notes — {demo.full_name}</h3>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Client</label>
            <div style={{ fontWeight: 600 }}>
              {demo.full_name}{' '}
              <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>/ {demo.company}</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Contact</label>
            <div>{demo.email} · {demo.phone_no}</div>
          </div>
          <div className="form-group">
            <label className="form-label">Demo Scheduled</label>
            <div style={{ fontWeight: 600, color: 'var(--primary)' }}>
              {displayBookingDate(demo.booking_date || demo.date || demo.demo_date || demo.demoDate)}
            </div>
            <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
              {displayBookingTime(demo.booking_time || demo.time || demo.demo_time || demo.demoTime)}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <div style={{
              fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.8,
              background: 'var(--border-light)', padding: '14px 16px', borderRadius: 8
            }}>
              {demo.notes || 'No notes available.'}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function DemoSchedules() {
  const toast = useToast();
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDemo, setViewDemo] = useState(null);

  // ── GET /booking/getDemoBookings ──────────────────────────────────────────
  const fetchDemos = async () => {
    try {
      setLoading(true);
      const res = await api.get('/booking/getDemoBookings');
      const raw = res.data;
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw?.bookings)
            ? raw.bookings
            : Array.isArray(raw?.result)
              ? raw.result
              : [];

      console.log('[DemoSchedules] total count:', list.length);
      console.log('[DemoSchedules] first item keys:', list[0] ? Object.keys(list[0]) : 'empty');
      console.log('[DemoSchedules] first item:', list[0]);
      console.log('[DemoSchedules] raw booking_date value:', list[0]?.booking_date || list[0]?.date || list[0]?.demo_date);
      // Show newest first — slice a copy so original order is preserved
      setDemos([...list].reverse());
    } catch (err) {
      console.error('[DemoSchedules] fetch error:', err);
      toast.error('Load Failed', 'Failed to load demo schedules. Please try again.');
      setDemos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemos();
  }, []);

  // ── POST /booking/deleteDemoBooking ───────────────────────────────────────
  const handleDelete = async (demo) => {
    const ok = await toast.confirm(`This will permanently delete the demo booking for "${demo.full_name}".`, { confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await api.post('/booking/deleteDemoBooking', { id: demo.id });
      setDemos(prev => prev.filter(d => d.id !== demo.id));
      toast.success('Deleted!', `Demo booking for "${demo.full_name}" has been removed.`);
    } catch (err) {
      toast.error('Delete Failed', 'Failed to delete demo. Please try again.');
    }
  };

  const safeList = Array.isArray(demos) ? demos : [];

  return (
    <div className="page-content">
      <div className="page-header">
        <h2>Demo Schedules</h2>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(1, 1fr)', maxWidth: 200 }}>
        <div className="stat-card" style={{ '--card-accent': '#8B5CF6', '--card-icon-bg': 'rgba(139,92,246,0.12)' }}>
          <div className="stat-card-header">
            <span className="stat-label">Total Demos</span>
            <div className="stat-icon"><CalendarClock size={18} /></div>
          </div>
          <div className="stat-value">{safeList.length}</div>
        </div>
      </div>

      {/* Table */}
      <div className="table-card">
        <div className="table-card-header">
          <h3>All Demo Schedules ({safeList.length})</h3>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th>Notes</th>
                <th>Demo Date</th>
                <th>Time</th>
                <th>Action</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10}>
                    <div className="empty-state"><p>Loading demo schedules...</p></div>
                  </td>
                </tr>
              ) : safeList.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="empty-state"><CalendarClock size={40} /><p>No demo schedules yet.</p></div>
                  </td>
                </tr>
              ) : safeList.map((demo, i) => (
                <tr key={demo.id}>
                  <td><strong>{i + 1}</strong></td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{demo.full_name}</td>
                  <td style={{ fontSize: 12.5 }}>{demo.email}</td>
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{demo.phone_no}</td>
                  <td>{demo.company}</td>
                  <td>
                    <button className="action-btn view" onClick={() => setViewDemo(demo)} title="View Notes">
                      <StickyNote size={15} />
                    </button>
                  </td>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {displayBookingDate(demo.booking_date || demo.date || demo.demo_date || demo.demoDate)}
                  </td>
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {displayBookingTime(demo.booking_time || demo.time || demo.demo_time || demo.demoTime)}
                  </td>
                  <td>
                    <div className="action-btns">
                      <button
                        className="action-btn delete"
                        onClick={() => handleDelete(demo)}
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    {(() => {
                      const raw = demo.createdAt || demo.created_at || demo.createdDate;
                      const f = formatCreatedDate(raw);
                      return f ? (
                        <>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>{f.date}</div>
                          <div style={{ fontSize: 11, color: '#3B82F6' }}>🇮🇳 IST &nbsp;{f.ist}</div>
                          <div style={{ fontSize: 11, color: '#8B5CF6', marginTop: 2 }}>🇺🇸 EST &nbsp;{f.us}</div>
                        </>
                      ) : '—';
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewDemo && <NotesModal demo={viewDemo} onClose={() => setViewDemo(null)} />}
    </div>
  );
}
